<script lang="ts">
  import type { BuildSummary } from '../build-state.svelte.js';
  import { i18next } from '../i18n.js';
  import { lang as langState } from '../lang-state.svelte.js';

  interface Props {
    summary: BuildSummary | null;
    overrides: Record<string, number>;
    onSet: (stat: string, value: number | null) => void;
    onClearAll: () => void;
  }

  let { summary, overrides, onSet, onClearAll }: Props = $props();
  let lang = $derived(langState.current);

  const ADDITIVE_STATS: Array<{ key: string; suffix: string }> = [
    { key: 'wd', suffix: '%' },
    { key: 'wd_ar', suffix: '%' }, { key: 'wd_smg', suffix: '%' }, { key: 'wd_lmg', suffix: '%' },
    { key: 'wd_mmr', suffix: '%' }, { key: 'wd_rifle', suffix: '%' },
    { key: 'wd_shotgun', suffix: '%' }, { key: 'wd_pistol', suffix: '%' },
    { key: 'chc', suffix: '%' }, { key: 'chd', suffix: '%' }, { key: 'hsd', suffix: '%' },
    { key: 'rof', suffix: '%' }, { key: 'mag', suffix: '%' }, { key: 'mag_flat', suffix: '' },
    { key: 'reload', suffix: '%' }, { key: 'handling', suffix: '%' },
    { key: 'dth', suffix: '%' }, { key: 'dta', suffix: '%' }, { key: 'ooc', suffix: '%' },
  ];

  const WEAPON_STATS: Array<{ key: string; suffix: string }> = [
    { key: 'baseDamage', suffix: '' },
    { key: 'magazine', suffix: '' },
    { key: 'rpm', suffix: '' },
    { key: 'reloadSeconds', suffix: 's' },
  ];

  function statName(s: string): string {
    void lang;
    const fallback = s.toUpperCase();
    return i18next.t(s, { ns: 'stats', defaultValue: fallback }) as string;
  }

  function additiveValue(key: string): number {
    if (!summary) return 0;
    const a = summary.additive as Record<string, number>;
    return a[key] ?? 0;
  }

  function weaponValue(key: string): number {
    if (!summary?.dpsInput?.weapon) return 0;
    const w = summary.dpsInput.weapon as Record<string, number>;
    return w[key] ?? 0;
  }

  function fmt(n: number, key: string): string {
    if (key === 'baseDamage') return Math.round(n).toLocaleString();
    if (key === 'magazine' || key === 'rpm') return String(Math.round(n));
    if (key === 'reloadSeconds') return n.toFixed(2);
    return n.toFixed(1);
  }

  function setOverride(stat: string, raw: string) {
    if (raw === '') {
      onSet(stat, null);
    } else {
      const n = parseFloat(raw);
      if (Number.isFinite(n)) onSet(stat, n);
    }
  }

  let count = $derived(Object.keys(overrides).length);
</script>

<section class="panel mop">
  <div class="panel-title">
    <span>{lang === 'ru' ? 'Корректировка значений (PRO)' : 'Stat overrides (PRO)'}</span>
    {#if count > 0}
      <button class="reset" onclick={() => onClearAll()}>{lang === 'ru' ? 'Сбросить все' : 'Reset all'} ({count})</button>
    {/if}
  </div>

  <div class="hint">
    {lang === 'ru'
      ? 'Точечная подмена значения: вводи финальное число — оно заменит вычисленное (без перехода в Stats режим). DPS пересчитается. Пусто = без override.'
      : 'Point-fix override: type the final number you see in-game — it replaces the computed value (without switching to Stats mode). DPS recomputes. Empty = no override.'}
  </div>

  <table class="ovr-tbl">
    <thead>
      <tr>
        <th>{lang === 'ru' ? 'Стат' : 'Stat'}</th>
        <th class="num">{lang === 'ru' ? 'Расчёт' : 'Computed'}</th>
        <th class="num">{lang === 'ru' ? 'Override' : 'Override'}</th>
        <th class="num">{lang === 'ru' ? 'Итог' : 'Final'}</th>
      </tr>
    </thead>
    <tbody>
      <tr class="group-row"><td colspan="4">{lang === 'ru' ? '— Аддитивные —' : '— Additive —'}</td></tr>
      {#each ADDITIVE_STATS as row (row.key)}
        {@const computed = additiveValue(row.key)}
        {@const ovrVal = overrides[row.key]}
        {@const overridden = ovrVal !== undefined && Number.isFinite(ovrVal)}
        {@const final = overridden ? ovrVal : computed}
        <tr class:overridden>
          <td>
            {#if overridden}<span class="warn">⚠</span>{/if}
            {statName(row.key)}
          </td>
          <td class="num dim">{fmt(computed, row.key)}{row.suffix}</td>
          <td class="num">
            <input
              type="number"
              step="0.1"
              class="input ovr-in"
              placeholder="—"
              value={overridden ? ovrVal : ''}
              oninput={(e) => setOverride(row.key, (e.currentTarget as HTMLInputElement).value)}
            />
          </td>
          <td class="num final">{fmt(final, row.key)}{row.suffix}</td>
        </tr>
      {/each}

      <tr class="group-row"><td colspan="4">{lang === 'ru' ? '— Оружие —' : '— Weapon —'}</td></tr>
      {#each WEAPON_STATS as row (row.key)}
        {@const computed = weaponValue(row.key)}
        {@const ovrVal = overrides[row.key]}
        {@const overridden = ovrVal !== undefined && Number.isFinite(ovrVal)}
        {@const final = overridden ? ovrVal : computed}
        <tr class:overridden>
          <td>
            {#if overridden}<span class="warn">⚠</span>{/if}
            {statName(row.key)}
          </td>
          <td class="num dim">{fmt(computed, row.key)}{row.suffix}</td>
          <td class="num">
            <input
              type="number"
              step={row.key === 'reloadSeconds' ? '0.01' : '1'}
              class="input ovr-in"
              placeholder="—"
              value={overridden ? ovrVal : ''}
              oninput={(e) => setOverride(row.key, (e.currentTarget as HTMLInputElement).value)}
            />
          </td>
          <td class="num final">{fmt(final, row.key)}{row.suffix}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  .mop {
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-left: 3px solid var(--orange);
    border-radius: var(--r);
    padding: 8px 10px;
    margin-top: 8px;
  }
  .panel-title {
    display: flex; justify-content: space-between; align-items: center;
  }
  .reset {
    background: var(--raised);
    border: 1px solid var(--border);
    color: var(--orange);
    padding: 3px 8px;
    border-radius: 3px;
    font: 700 9px/1 var(--f-display);
    letter-spacing: .1em;
    cursor: pointer;
  }
  .reset:hover { border-color: var(--orange); background: rgba(254,175,16,.15); }

  .hint {
    margin: 6px 0 8px;
    font-size: 10px;
    color: var(--text-dim);
    line-height: 1.4;
  }

  .ovr-tbl {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    font-family: var(--f-mono);
  }
  .ovr-tbl th {
    text-align: left;
    padding: 4px 6px;
    border-bottom: 1px solid var(--border);
    font: 700 9px/1 var(--f-display);
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .ovr-tbl th.num { text-align: right; }
  .ovr-tbl td {
    padding: 3px 6px;
    border-bottom: 1px dashed var(--border);
  }
  .ovr-tbl td.num { text-align: right; }
  .ovr-tbl td.dim { color: var(--text-dim); }
  .ovr-tbl td.final { color: var(--green); font-weight: 600; }
  .ovr-tbl tr.overridden { background: rgba(254,175,16,.06); }
  .ovr-tbl tr.overridden td.final { color: var(--orange); }
  .ovr-tbl tr.group-row td {
    color: var(--muted);
    font: 700 9px/1 var(--f-display);
    letter-spacing: .12em;
    text-transform: uppercase;
    padding: 6px 0 4px;
    border: none;
  }

  .warn { color: var(--orange); margin-right: 4px; }
  .ovr-in {
    width: 80px;
    padding: 3px 6px;
    font-size: 11px;
    font-family: var(--f-mono);
    text-align: right;
  }
  .ovr-in::-webkit-outer-spin-button,
  .ovr-in::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
</style>
